import type { Role } from "../auth/models/role.model";
import { PERMISSIONS } from "./permissions";

const p = PERMISSIONS;

function role(
  id: string,
  name: string,
  slug: string,
  permissions: Role["permissions"],
  description?: string
): Role {
  return { id, name, slug, permissions, description, isSystem: true };
}

/** Rôles tenant */
const DEPARTMENT_POSITION_ORG_ADMIN = [
  p.department.read,
  p.department.create,
  p.department.update,
  p.department.delete,
  p.position.read,
  p.position.create,
  p.position.update,
  p.position.delete,
  p.organization.read,
  p.organization.manage,
];

const DEPARTMENT_POSITION_ORG_HR = [
  p.department.read,
  p.department.create,
  p.department.update,
  p.position.read,
  p.position.create,
  p.position.update,
  p.organization.read,
];

const LEAVE_EMPLOYEE = [p.leave.read, p.leave.create];

const LEAVE_FULL = [
  p.leave.read,
  p.leave.create,
  p.leave.update,
  p.leave.approve,
  p.leave.manage,
];

const ATTENDANCE_FULL = [
  p.attendance.read,
  p.attendance.create,
  p.attendance.update,
  p.attendance.manage,
  p.attendance.analytics,
];

const SCHEDULE_FULL = [
  p.schedule.read,
  p.schedule.create,
  p.schedule.update,
  p.schedule.manage,
];

const PERFORMANCE_FULL = [
  p.performance.read,
  p.performance.create,
  p.performance.update,
  p.performance.review,
  p.performance.manage,
];

const COMPENSATION_HR = [
  p.compensation.read,
  p.bonus.read,
  p.bonus.manage,
];

const COMPENSATION_FULL = [
  p.compensation.read,
  p.compensation.manage,
  p.bonus.read,
  p.bonus.manage,
  p.payroll.adjust,
];

const TRAINING_FULL = [
  p.training.read,
  p.training.manage,
  p.course.read,
  p.course.manage,
  p.certification.read,
  p.certification.manage,
];

const CAREER_FULL = [
  p.career.read,
  p.career.manage,
  p.promotion.read,
  p.promotion.manage,
  p.succession.read,
  p.succession.manage,
];

const RECRUITMENT_FULL = [
  p.recruitment.read,
  p.recruitment.manage,
  p.candidate.read,
  p.candidate.manage,
  p.hiring.manage,
];

const ASSET_FULL = [
  p.asset.read,
  p.asset.create,
  p.asset.update,
  p.asset.assign,
  p.asset.maintain,
  p.asset.retire,
];

const SECURITY_FULL = [p.security.read, p.security.manage];

const ITSM_FULL = [
  p.ticket.read,
  p.ticket.create,
  p.ticket.update,
  p.ticket.assign,
  p.ticket.close,
  p.incident.read,
  p.incident.create,
  p.incident.resolve,
  p.sla.read,
  p.sla.manage,
];

const MSP_PROBLEM_FULL = [
  p.problem.read,
  p.problem.create,
  p.problem.update,
  p.problem.close,
];

const MSP_CHANGE_FULL = [
  p.change.read,
  p.change.create,
  p.change.approve,
];

const MSP_KNOWLEDGE_FULL = [
  p.knowledge.read,
  p.knowledge.create,
  p.knowledge.publish,
];

const MSP_PROJECT_FULL = [
  p.project.read,
  p.project.create,
  p.project.manage,
];

const CONTRACTS_CUSTOMER_FULL = [p.customer.read, p.customer.create];

const CONTRACTS_COMMERCIAL_FULL = [
  p.contract.read,
  p.contract.create,
  p.contract.manage,
];

const CONTRACTS_SERVICE_FULL = [p.service.read, p.service.manage];

const CONTRACTS_BILLING_FULL = [p.billing.read, p.billing.manage];

const REQUEST_FULL = [
  p.request.read,
  p.request.create,
  p.request.approve,
  p.request.complete,
];

const APPROVAL_FULL = [p.approval.read, p.approval.manage];

const AUTOMATION_FULL = [p.automation.read, p.automation.manage];

const REQUEST_CUSTOMER = [p.request.read, p.request.create];

export const EMPLOYEE = role("role-employee", "Employee", "EMPLOYEE", [
  p.employee.read,
  p.attendance.read,
  p.performance.read,
  p.training.read,
  p.course.read,
  p.certification.read,
  p.career.read,
  p.asset.read,
  p.ticket.read,
  p.ticket.create,
  p.knowledge.read,
  ...LEAVE_EMPLOYEE,
]);

export const MANAGER = role("role-manager", "Manager", "MANAGER", [
  ...EMPLOYEE.permissions,
  p.employee.update,
  p.leave.approve,
  p.attendance.manage,
  p.actionValidation.read,
  p.actionValidation.approve,
]);

export const HR_MANAGER = role(
  "role-hr-manager",
  "HR Manager",
  "HR_MANAGER",
  [
    ...MANAGER.permissions,
    p.employee.create,
    p.employee.delete,
    ...DEPARTMENT_POSITION_ORG_HR,
    ...LEAVE_FULL,
    ...ATTENDANCE_FULL,
    ...SCHEDULE_FULL,
    ...PERFORMANCE_FULL,
    ...COMPENSATION_HR,
    p.payroll.read,
    p.payroll.run,
    p.payroll.adjust,
    ...TRAINING_FULL,
    ...CAREER_FULL,
    ...RECRUITMENT_FULL,
    p.asset.read,
    p.user.read,
    p.user.create,
    p.user.update,
    p.role.read,
    p.invitation.read,
    p.invitation.create,
    p.actionValidation.read,
    p.actionValidation.approve,
  ]
);

export const PAYROLL_MANAGER = role(
  "role-payroll-manager",
  "Payroll Manager",
  "PAYROLL_MANAGER",
  [
    p.employee.read,
    p.payroll.read,
    p.payroll.run,
    p.payroll.adjust,
    p.compensation.read,
    p.actionValidation.read,
    p.actionValidation.approve,
  ]
);

export const RECRUITER = role("role-recruiter", "Recruiter", "RECRUITER", [
  ...RECRUITMENT_FULL,
  p.actionValidation.read,
  p.actionValidation.approve,
]);

/** Aligné sur IAM `HR_OPERATOR` (manifest rh) — connexion locale hr@itaas.demo */
export const HR_OPERATOR = role(
  "role-hr-operator",
  "HR Operator",
  "HR_OPERATOR",
  [p.employee.read, p.leave.read, p.recruitment.read],
  "Opérateur RH — lecture employés, congés et recrutement"
);

export const CRM_MANAGER = role(
  "role-crm-manager",
  "CRM Manager",
  "CRM_MANAGER",
  [p.crm.read, p.crm.manage]
);

export const DEALS_MANAGER = role(
  "role-deals-manager",
  "Deals Manager",
  "DEALS_MANAGER",
  [p.crm.read, p.crm.manage],
  "Responsable pipeline opportunités"
);

export const LEADS_MANAGER = role(
  "role-leads-manager",
  "Leads Manager",
  "LEADS_MANAGER",
  [p.crm.read, p.crm.manage],
  "Responsable prospection et leads"
);

export const ATTENDANCE_MANAGER = role(
  "role-attendance-manager",
  "Attendance Manager",
  "ATTENDANCE_MANAGER",
  [p.employee.read, ...ATTENDANCE_FULL],
  "Responsable présence et pointage"
);

export const FINANCE_MANAGER = role(
  "role-finance-manager",
  "Finance Manager",
  "FINANCE_MANAGER",
  [
    p.billing.read,
    p.billing.manage,
    p.contract.read,
    p.customer.read,
    p.service.read,
  ],
  "Responsable finance et facturation"
);

export const ASSET_MANAGER = role(
  "role-asset-manager",
  "Asset Manager",
  "ASSET_MANAGER",
  [...ASSET_FULL],
  "Responsable parc actifs"
);

export const HELP_DESK_MANAGER = role(
  "role-help-desk-manager",
  "Help Desk Manager",
  "HELP_DESK_MANAGER",
  [
    p.ticket.read,
    p.ticket.create,
    p.ticket.update,
    p.ticket.assign,
    p.ticket.close,
    p.incident.read,
    p.knowledge.read,
  ],
  "Responsable support et help desk"
);

export const IT_ADMIN = role("role-it-admin", "IT Admin", "IT_ADMIN", [
  ...ITSM_FULL,
  ...MSP_PROBLEM_FULL,
  ...MSP_CHANGE_FULL,
  ...MSP_KNOWLEDGE_FULL,
  ...REQUEST_FULL,
  ...APPROVAL_FULL,
  p.user.manage,
  p.settings.manage,
  ...ASSET_FULL,
]);

export const SECURITY_MANAGER = role(
  "role-security-manager",
  "Security Manager",
  "SECURITY_MANAGER",
  [...SECURITY_FULL],
  "Responsable operations securite privee"
);

export const PROJECT_MANAGER = role(
  "role-project-manager",
  "Project Manager",
  "PROJECT_MANAGER",
  [...MSP_PROJECT_FULL, p.change.read]
);

export const ACCOUNT_MANAGER = role(
  "role-account-manager",
  "Account Manager",
  "ACCOUNT_MANAGER",
  [
    ...CONTRACTS_CUSTOMER_FULL,
    ...CONTRACTS_COMMERCIAL_FULL,
    p.service.read,
    p.billing.read,
  ]
);

const PORTAL_CUSTOMER_FULL = [
  p.portal.ticketRead,
  p.portal.assetRead,
  p.portal.contractRead,
  p.portal.invoiceRead,
  p.portal.userRead,
];

export const CUSTOMER_USER = role(
  "role-customer-user",
  "Customer User",
  "CUSTOMER_USER",
  [...PORTAL_CUSTOMER_FULL, ...REQUEST_CUSTOMER],
  "Utilisateur portail client MSP"
);

export const TENANT_ADMIN = role(
  "role-tenant-admin",
  "Tenant Admin",
  "TENANT_ADMIN",
  [
    ...HR_MANAGER.permissions,
    ...PAYROLL_MANAGER.permissions,
    ...CRM_MANAGER.permissions,
    ...IT_ADMIN.permissions,
    ...SECURITY_FULL,
    ...PROJECT_MANAGER.permissions,
    ...ACCOUNT_MANAGER.permissions,
    ...CONTRACTS_BILLING_FULL,
    ...CONTRACTS_SERVICE_FULL,
    p.automation.read,
    p.compensation.manage,
    ...DEPARTMENT_POSITION_ORG_ADMIN,
    p.user.read,
    p.user.create,
    p.user.update,
    p.user.delete,
    p.role.read,
    p.role.manage,
    p.invitation.read,
    p.invitation.create,
    p.invitation.cancel,
    p.user.manage,
    p.settings.manage,
  ]
);

/** Rôles plateforme (super-admin) */
export const SUPPORT_AGENT = role(
  "role-support-agent",
  "Support Agent",
  "SUPPORT_AGENT",
  [p.support.manage, p.ticket.read, p.ticket.manage, p.tenant.manage],
  "Agent support plateforme"
);

export const BILLING_MANAGER = role(
  "role-billing-manager",
  "Billing Manager",
  "BILLING_MANAGER",
  [p.billing.manage, p.subscription.manage],
  "Gestion facturation et abonnements"
);

export const PLATFORM_ADMIN = role(
  "role-platform-admin",
  "Platform Admin",
  "PLATFORM_ADMIN",
  [
    p.platform.manage,
    p.tenant.manage,
    p.subscription.manage,
    p.billing.manage,
    p.support.manage,
    p.user.read,
    p.user.create,
    p.user.update,
    p.user.delete,
    p.role.read,
    p.role.manage,
    p.invitation.read,
    p.invitation.create,
    p.invitation.cancel,
    ...DEPARTMENT_POSITION_ORG_ADMIN,
    ...LEAVE_FULL,
    ...ATTENDANCE_FULL,
    ...SCHEDULE_FULL,
    ...PERFORMANCE_FULL,
    ...COMPENSATION_FULL,
    ...TRAINING_FULL,
    ...CAREER_FULL,
    ...RECRUITMENT_FULL,
    ...ASSET_FULL,
    ...ITSM_FULL,
    ...MSP_PROBLEM_FULL,
    ...MSP_CHANGE_FULL,
    ...MSP_KNOWLEDGE_FULL,
    ...MSP_PROJECT_FULL,
    ...CONTRACTS_CUSTOMER_FULL,
    ...CONTRACTS_COMMERCIAL_FULL,
    ...CONTRACTS_SERVICE_FULL,
    ...CONTRACTS_BILLING_FULL,
    ...REQUEST_FULL,
    ...APPROVAL_FULL,
    ...AUTOMATION_FULL,
  ],
  "Administration plateforme"
);

export const PLATFORM_OWNER = role(
  "role-platform-owner",
  "Platform Owner",
  "PLATFORM_OWNER",
  [
    ...PLATFORM_ADMIN.permissions,
    p.platform.manage,
  ],
  "Propriétaire plateforme SaaS"
);

/** Alias legacy super-admin */
export const SUPER_ADMIN = PLATFORM_ADMIN;

export const SYSTEM_ROLES: Record<string, Role> = {
  EMPLOYEE,
  MANAGER,
  HR_MANAGER,
  HR_OPERATOR,
  PAYROLL_MANAGER,
  RECRUITER,
  CRM_MANAGER,
  DEALS_MANAGER,
  LEADS_MANAGER,
  ATTENDANCE_MANAGER,
  FINANCE_MANAGER,
  ASSET_MANAGER,
  HELP_DESK_MANAGER,
  IT_ADMIN,
  SECURITY_MANAGER,
  PROJECT_MANAGER,
  ACCOUNT_MANAGER,
  CUSTOMER_USER,
  TENANT_ADMIN,
  SUPER_ADMIN,
  PLATFORM_OWNER,
  PLATFORM_ADMIN,
  SUPPORT_AGENT,
  BILLING_MANAGER,
};

export function getRoleBySlug(slug: string): Role | undefined {
  return SYSTEM_ROLES[slug];
}

export function resolveRolesByIds(roleIds: string[]): Role[] {
  return roleIds
    .map((id) => Object.values(SYSTEM_ROLES).find((r) => r.id === id))
    .filter((r): r is Role => Boolean(r));
}

export function resolveRolesBySlugs(slugs: string[]): Role[] {
  return slugs
    .map((slug) => SYSTEM_ROLES[slug])
    .filter((r): r is Role => Boolean(r));
}
